import React, { useId, type FocusEvent } from "react";
import clsx from "clsx";

import styles from "./radio-group.module.css";

export interface RadioGroupOption {
  value: string;
  label: string;
}

export interface RadioGroupProps {
  id?: string;
  name: string;
  label: string;
  required?: boolean;
  options: RadioGroupOption[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
  layout?: "flex" | "grid";
}

export function RadioGroup({
  id: idProp,
  name,
  label,
  required = false,
  options,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  orientation = "horizontal",
  layout = "flex",
}: RadioGroupProps) {
  const reactId = useId().replace(/:/g, "");
  const baseId = idProp ?? `radio-group-${reactId}`;
  const errorId = `${baseId}-error`;
  const hasError = error != null && error !== "";
  const vertical = orientation === "vertical";

  const handleBlur = (e: FocusEvent<HTMLFieldSetElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next != null && e.currentTarget.contains(next)) return;
    onBlur?.();
  };

  return (
    <fieldset
      className={styles.wrapper}
      disabled={disabled}
      onBlur={handleBlur}
      aria-invalid={hasError ? true : undefined}
      aria-describedby={hasError ? errorId : undefined}
    >
      <legend className={clsx("sub-heading-md", styles.label)}>
        {label}
        {required ? (
          <span className="text-danger text-nowrap" aria-hidden>
            {"\u00A0*"}
          </span>
        ) : null}
      </legend>
      <div
        className={clsx(
          styles.options,
          vertical && styles.optionsVertical,
          layout === "grid" && styles.optionsGrid,
        )}
        role="presentation"
      >
        {options.map((opt, index) => {
          const inputId = `${baseId}-${opt.value}`;
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              className={clsx(
                styles.option,
                disabled && styles.optionDisabled,
                selected && styles.optionSelected,
              )}
              htmlFor={inputId}
            >
              <input
                id={inputId}
                className={styles.nativeInput}
                type="radio"
                name={name}
                value={opt.value}
                checked={selected}
                required={required && index === 0}
                disabled={disabled}
                onChange={() => onChange(opt.value)}
              />
              <span className={styles.radioMark} aria-hidden />
              <span className="body-sm">{opt.label}</span>
            </label>
          );
        })}
      </div>
      {hasError ? (
        <p id={errorId} className={clsx(styles.message, styles.messageError)} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
